import { createApp, ref, onMounted } from 'vue';

import { useArticles } from './hooks';

export const youkai = () => {
  const youkaiApp = document.getElementById('youkai');
  if (!youkaiApp) return;

  const app = createApp({
    setup() {
      const root = ref<HTMLElement | null>(null);
      const isInit = ref(false);
      const level = ref<number>(1);
      const id = ref<string | null>('0');
      const slug = ref<string | null>('all');
      const name = ref<string | null>('all');
      const prefectureId = ref<string | null>('0');
      const prefectureSlug = ref<string | null>(null);
      const prefectureName = ref<string | null>(null);
      const YOUKAI_PATH_NAME = '/entertainment/yokai/';

      const {
        posts,
        totalCount,
        canFetch,
        isLoading,
        isError,
        fetchPosts,
        parentCategoryId,
        parentCategorySlug,
        parentCategoryName,
      } = useArticles();

      const switchToFirstLevel = async () => {
        const switchMap = () => {
          id.value = '0';
          level.value = 1;
          slug.value = 'all';
          name.value = 'all';
        };

        const pageTransition = () => {
          window.history.pushState('', '', YOUKAI_PATH_NAME);
        };

        await fetchPosts('/wp-json/wp/v2/yokai/articles');
        switchMap();
        pageTransition();
      };

      const switchToSecondLevel = async (e: MouseEvent) => {
        const eventTarget = e.currentTarget as HTMLLinkElement;
        const isBack = level.value === 3;
        const categoryId = isBack ? id.value : eventTarget?.dataset.id;
        if (!categoryId) return;

        const switchMap = () => {
          id.value = categoryId;
          level.value = 2;
          if (!isBack) {
            slug.value = eventTarget?.dataset.slug || null;
            name.value = eventTarget?.dataset.name || null;
          }
          prefectureId.value = null;
          prefectureSlug.value = null;
          prefectureName.value = null;
        };

        const pageTransition = () => {
          window.history.pushState('', '', `${YOUKAI_PATH_NAME}${slug.value}/`);
        };

        await fetchPosts(`/wp-json/wp/v2/yokai/areas/${categoryId}`);
        switchMap();
        pageTransition();
      };

      const switchToThirdLevel = async (e: MouseEvent) => {
        const eventTarget = e.currentTarget as HTMLLinkElement;
        const categoryId = eventTarget?.dataset.id;
        if (!categoryId) return;

        const switchMap = () => {
          level.value = 3;
          prefectureId.value = eventTarget?.dataset.id || null;
          prefectureSlug.value = eventTarget?.dataset.slug || null;
          prefectureName.value = eventTarget?.dataset.name || null;

          id.value = parentCategoryId.value;
          slug.value = parentCategorySlug.value;
          name.value = parentCategoryName.value;
        };

        const pageTransition = () => {
          window.history.pushState(
            '',
            '',
            `${YOUKAI_PATH_NAME}${parentCategorySlug.value}/${prefectureSlug.value}/`,
          );
        };

        await fetchPosts(`/wp-json/wp/v2/yokai/prefectures/${categoryId}`);
        switchMap();
        pageTransition();
      };

      const moreFetch = () => {
        if (level.value === 1) {
          fetchPosts('/wp-json/wp/v2/yokai/articles');
        } else if (level.value === 2) {
          fetchPosts(`/wp-json/wp/v2/yokai/areas/${id.value}`);
        } else {
          fetchPosts(`/wp-json/wp/v2/yokai/prefectures/${prefectureId.value}`);
        }
      };

      onMounted(() => {
        const firstFetch = async () => {
          const fetchId = root.value?.dataset.id;
          const fetchSlug = root.value?.dataset.slug;
          const fetchName = root.value?.dataset.name;
          const fetchLevel = root.value?.dataset.level;
          const isSecondLevel = fetchLevel === '2' && fetchId && fetchSlug && fetchName;
          const isThirdLevel = fetchLevel === '3' && fetchId && fetchSlug && fetchName;

          if (isThirdLevel) {
            const switchMap = () => {
              id.value = parentCategoryId.value;
              slug.value = parentCategorySlug.value;
              name.value = parentCategoryName.value;
              prefectureId.value = fetchId;
              prefectureSlug.value = fetchSlug;
              prefectureName.value = fetchName;
              level.value = 3;
            };

            await fetchPosts(`/wp-json/wp/v2/yokai/prefectures/${fetchId}`);
            switchMap();
          } else if (isSecondLevel) {
            const switchMap = () => {
              id.value = fetchId;
              slug.value = fetchSlug;
              name.value = fetchName;
              level.value = 2;
            };

            await fetchPosts(`/wp-json/wp/v2/yokai/areas/${fetchId}`);
            switchMap();
          } else {
            const switchMap = () => {
              id.value = '0';
              level.value = 1;
              slug.value = 'all';
              name.value = 'all';
            };

            await fetchPosts('/wp-json/wp/v2/yokai/articles');
            switchMap();
          }
          isInit.value = true;
        };

        firstFetch();
      });

      return {
        root,
        isInit,
        id,
        level,
        slug,
        name,
        prefectureSlug,
        prefectureName,
        switchToFirstLevel,
        switchToSecondLevel,
        switchToThirdLevel,
        posts,
        totalCount,
        canFetch,
        isLoading,
        isError,
        moreFetch,
      };
    },
  });

  app.mount('#youkai');
};
